import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, IconButton, LinearProgress, MenuItem, Paper, Select, Skeleton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  VisibilityOutlined as VisibilityIcon
} from '@mui/icons-material';
import ProductFilter from '../../Documents/ProductFilter';
import { apiBlobError, downloadBlob, fmtDateTime, plural } from '../../Documents/format';
import { notify } from '../../../notifications';
import { DEFAULT_SORT, EMPTY_FILTERS, EXPORT_COLUMNS, EXPORT_FORMATS } from '../constants';
import { isCancelled, markingApi, searchMaterials } from '../markingApi';
import { PERIOD_PRESETS, PeriodPreset, periodRange } from '../period';
import { ExportCount, ExportFormat, MarkingFilters, MarkingMeta, MarkingRow } from '../types';
import { IdMultiSelect, PlaceFilter } from './FilterControls';

interface Props {
  open: boolean;
  meta: MarkingMeta;
  /** Фильтры списка на момент открытия - выгрузка стартует с них */
  initial: MarkingFilters;
  onClose: () => void;
}

const COUNT_DELAY_MS = 300;
const PREVIEW_ROWS = 3;
const BORDER = '#e2e8f0';

const ALL_COLUMN_KEYS = EXPORT_COLUMNS.map(c => c.key);

const sectionLabel = { mb: 0.75, fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary' } as const;

/** Значение ячейки предпросмотра: те же поля и порядок, что попадут в файл */
function previewCell(key: string, row: MarkingRow): string {
  switch (key) {
    case 'code': return row.code;
    case 'product': return row.product || '-';
    case 'type': return row.typeName || '-';
    case 'status': return row.statusName || '-';
    case 'documentNumber': return `#${row.documentId}`;
    case 'sender': return row.sender || '-';
    case 'receiver': return row.recipient || '-';
    case 'date': return fmtDateTime(row.date);
    default: return '-';
  }
}

const MarkingCodesExportDialog: React.FC<Props> = ({ open, meta, initial, onClose }) => {
  const [filters, setFilters] = useState<MarkingFilters>(EMPTY_FILTERS);
  const [preset, setPreset] = useState<PeriodPreset>('all');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [columns, setColumns] = useState<string[]>(ALL_COLUMN_KEYS);
  const [showColumns, setShowColumns] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [count, setCount] = useState<ExportCount | null>(null);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<MarkingRow[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const downloadCtrl = useRef<AbortController | null>(null);

  const pharmacyOptions = useMemo(() => meta.pharmacies.filter(p => p.kind === 'pharmacy'), [meta.pharmacies]);
  const storageOptions = useMemo(() => meta.pharmacies.filter(p => p.kind === 'warehouse'), [meta.pharmacies]);

  // Открытие диалога: стартуем с фильтров списка
  useEffect(() => {
    if (!open) return;
    setFilters(initial);
    setPreset(initial.dateFrom || initial.dateTo ? 'custom' : 'all');
    setFormat('xlsx');
    setColumns(ALL_COLUMN_KEYS);
    setShowColumns(false);
    setShowPreview(false);
    setCount(null);
    setCountError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Незавершённая выгрузка отменяется при закрытии окна и размонтировании
  useEffect(() => () => downloadCtrl.current?.abort(), []);

  // Счётчик записей (и файлов) по текущим фильтрам
  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setCounting(true);
    const timer = window.setTimeout(() => {
      markingApi.exportCount(filters, ctrl.signal)
        .then(res => { setCount(res); setCountError(null); setCounting(false); })
        .catch(err => {
          if (isCancelled(err)) return;
          setCountError('Не удалось подсчитать записи');
          setCounting(false);
        });
    }, COUNT_DELAY_MS);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [open, filters]);

  // Предпросмотр - только когда он развёрнут, чтобы не нагружать базу лишними запросами
  useEffect(() => {
    if (!open || !showPreview) return undefined;
    const ctrl = new AbortController();
    setPreviewLoading(true);
    const timer = window.setTimeout(() => {
      markingApi.list(filters, DEFAULT_SORT, 1, PREVIEW_ROWS, ctrl.signal)
        .then(res => { setPreviewRows(res.codes); setPreviewLoading(false); })
        .catch(err => {
          if (isCancelled(err)) return;
          setPreviewRows([]);
          setPreviewLoading(false);
        });
    }, COUNT_DELAY_MS);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [open, showPreview, filters]);

  const patch = useCallback((next: Partial<MarkingFilters>) => setFilters(prev => ({ ...prev, ...next })), []);

  const changePreset = useCallback((value: PeriodPreset) => {
    setPreset(value);
    if (value === 'all') { patch({ dateFrom: '', dateTo: '' }); return; }
    const range = periodRange(value);
    if (range) patch({ dateFrom: range.from, dateTo: range.to });
  }, [patch]);

  const toggleColumn = useCallback((key: string) => {
    // порядок колонок в файле всегда как в справочнике, а не по порядку кликов
    setColumns(prev => ALL_COLUMN_KEYS.filter(k => (k === key ? prev.indexOf(k) === -1 : prev.indexOf(k) !== -1)));
  }, []);

  const reset = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPreset('all');
    setColumns(ALL_COLUMN_KEYS);
    setFormat('xlsx');
  }, []);

  const close = useCallback(() => {
    downloadCtrl.current?.abort();
    onClose();
  }, [onClose]);

  const download = useCallback(async () => {
    const ctrl = new AbortController();
    downloadCtrl.current = ctrl;
    setDownloading(true);
    try {
      const file = await markingApi.exportFile(filters, format, columns, ctrl.signal);
      downloadBlob(file.filename, file.blob);
      onClose();
    } catch (err) {
      if (!isCancelled(err)) notify.error(await apiBlobError(err, 'Не удалось сформировать выгрузку'));
    } finally {
      downloadCtrl.current = null;
      setDownloading(false);
    }
  }, [filters, format, columns, onClose]);

  const shownColumns = EXPORT_COLUMNS.filter(c => columns.indexOf(c.key) !== -1);
  const files = count?.files ?? 0;
  const tooLarge = Boolean(count?.tooLarge);
  const empty = count !== null && count.count === 0;
  const canDownload = !downloading && !counting && count !== null && !tooLarge && !empty && columns.length > 0;

  return (
    <Dialog
      open={open} onClose={downloading ? undefined : close} maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 660, borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700}>Выгрузка кодов маркировки</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup
            size="small" exclusive value={format} disabled={downloading}
            onChange={(_, v: ExportFormat | null) => { if (v) setFormat(v); }}
            sx={{ height: 30, '& .MuiToggleButton-root': { px: 1.25, py: 0, fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' } }}
          >
            {EXPORT_FORMATS.map(f => <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <IconButton onClick={close} size="small" aria-label="Закрыть" sx={{ color: 'text.secondary', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="body2" sx={sectionLabel}>Период выгрузки</Typography>
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ flex: preset === 'custom' ? '0 0 200px' : '1 1 100%' }}>
              <Select value={preset} onChange={e => changePreset(e.target.value as PeriodPreset)} disabled={downloading}>
                {PERIOD_PRESETS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
            {preset === 'custom' && (
              <>
                <TextField
                  size="small" type="date" label="Дата с" value={filters.dateFrom} disabled={downloading}
                  onChange={e => patch({ dateFrom: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                />
                <TextField
                  size="small" type="date" label="Дата по" value={filters.dateTo} disabled={downloading}
                  onChange={e => patch({ dateTo: e.target.value })} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: filters.dateFrom || undefined }} sx={{ flex: 1 }}
                />
              </>
            )}
          </Box>
        </Box>

        <ProductFilter
          text="" products={filters.products} search={searchMaterials} productsOnly
          onChange={next => patch({ products: next.products })}
        />

        <PlaceFilter
          label="Аптеки" emptyHint="Все аптеки зоны" noOptions="Аптека не найдена" fullWidth minWidth={0}
          options={pharmacyOptions} value={filters.pharmacies} onChange={pharmacies => patch({ pharmacies })}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {storageOptions.length > 0 && (
            <PlaceFilter
              label="Склады" emptyHint="Все склады" noOptions="Склад не найден" fullWidth minWidth={0}
              options={storageOptions} value={filters.storages} onChange={storages => patch({ storages })}
            />
          )}
          <IdMultiSelect
            label="Тип накладной" allText="Все типы" fullWidth minWidth={0}
            options={meta.types} value={filters.types} onChange={types => patch({ types })}
          />
          <IdMultiSelect
            label="Статус накладной" allText="Все статусы" fullWidth minWidth={0}
            options={meta.statuses} value={filters.statuses} onChange={statuses => patch({ statuses })}
          />
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>Колонки в файле:</Typography>
              <Typography variant="caption" color="text.secondary">
                {columns.length === ALL_COLUMN_KEYS.length ? `Все (${ALL_COLUMN_KEYS.length})` : `Выбрано ${columns.length} из ${ALL_COLUMN_KEYS.length}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Button
                size="small" variant="text" startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                onClick={() => setShowPreview(v => !v)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', height: 26, borderRadius: 1.25, color: showPreview ? 'primary.main' : 'text.secondary' }}
              >
                {showPreview ? 'Скрыть предпросмотр' : 'Предпросмотр'}
              </Button>
              <Button
                size="small" variant="text" onClick={() => setShowColumns(v => !v)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', height: 26, borderRadius: 1.25, color: showColumns ? 'primary.main' : 'text.secondary' }}
              >
                {showColumns ? 'Свернуть' : 'Настроить…'}
              </Button>
            </Box>
          </Box>

          <Collapse in={showColumns} timeout={180} unmountOnExit>
            <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px solid ${BORDER}`, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {EXPORT_COLUMNS.map(col => {
                const checked = columns.indexOf(col.key) !== -1;
                return (
                  <Chip
                    key={col.key} label={col.label} size="small" clickable onClick={() => toggleColumn(col.key)}
                    icon={checked ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                    color={checked ? 'primary' : 'default'} variant={checked ? 'filled' : 'outlined'}
                    sx={{ borderRadius: 1.5, height: 28, fontSize: '0.75rem', fontWeight: checked ? 600 : 400 }}
                  />
                );
              })}
            </Box>
          </Collapse>

          <Collapse in={showPreview} timeout={200} unmountOnExit>
            <Paper variant="outlined" sx={{ mt: 1, borderRadius: 2, overflow: 'hidden', borderColor: BORDER }}>
              <TableContainer sx={{ maxHeight: 160 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {shownColumns.map(col => (
                        <TableCell key={col.key} sx={{ py: 0.9, px: 1.25, fontSize: '0.72rem', fontWeight: 700, color: '#475569', bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewLoading && [0, 1, 2].map(i => (
                      <TableRow key={i}>
                        {shownColumns.map(col => (
                          <TableCell key={col.key} sx={{ py: 0.8, px: 1.25 }}><Skeleton variant="text" width={col.key === 'code' ? 120 : 70} /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {!previewLoading && previewRows.map(row => (
                      <TableRow key={row.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        {shownColumns.map(col => (
                          <TableCell
                            key={col.key}
                            sx={{
                              py: 0.75, px: 1.25, fontSize: '0.74rem', whiteSpace: 'nowrap', maxWidth: col.key === 'product' ? 190 : undefined,
                              overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: col.key === 'code' ? 'monospace' : undefined
                            }}
                          >
                            {previewCell(col.key, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {!previewLoading && !previewRows.length && (
                      <TableRow>
                        <TableCell colSpan={shownColumns.length || 1} align="center" sx={{ py: 2.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                          Нет данных по выбранным фильтрам
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Collapse>
        </Box>

        {tooLarge && count && (
          <Alert severity="warning">
            Записей больше допустимого предела выгрузки ({count.maxRows.toLocaleString('ru-RU')}). Сузьте период или добавьте фильтры.
          </Alert>
        )}
        {!tooLarge && count && count.files > 1 && (
          <Alert severity="info">
            Записей больше {count.partRows.toLocaleString('ru-RU')}: выгрузка будет разделена на {count.files} {plural(count.files, 'файл', 'файла', 'файлов')} и упакована в ZIP-архив.
          </Alert>
        )}
        {countError && <Alert severity="error">{countError}</Alert>}
        {downloading && (
          <Box>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Формируем файл. Большие выгрузки занимают несколько минут - окно закрывать не нужно.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, borderRadius: 1.5, bgcolor: 'rgba(25, 118, 210, 0.06)', height: 32 }}>
          <Typography variant="caption" color="text.secondary">Записей:</Typography>
          {counting
            ? <CircularProgress size={12} thickness={5} />
            : <Typography variant="caption" fontWeight={700} color="primary.main">{count ? count.count.toLocaleString('ru-RU') : '-'}</Typography>}
          {count && files > 1 && !counting && (
            <Tooltip title="Количество файлов в архиве">
              <Typography variant="caption" color="text.secondary">Файлов: {files}</Typography>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button size="small" color="inherit" onClick={reset} disabled={downloading} sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary', minWidth: 0, px: 1 }}>
            Сбросить
          </Button>
          <Button size="small" color="inherit" onClick={close} sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 0, px: 1.25 }}>
            Отмена
          </Button>
          <Button
            variant="contained" size="small" onClick={download} disabled={!canDownload}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', height: 34, px: 2, borderRadius: 1.5, whiteSpace: 'nowrap', boxShadow: 'none' }}
          >
            {downloading ? 'Формирование…' : files > 1 ? 'Скачать ZIP' : `Скачать ${format.toUpperCase()}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MarkingCodesExportDialog;
